import { createSlice } from '@reduxjs/toolkit'
import {
  HAStoreName,
  DateHeader,
  Causal,
  Quantity,
  Sizes,
  DocumentationNumber,
  Status,
  Reference,
  UserDetail,
  EditDate,
  OperationId,
  EnableMovementLog,
  ProgressiveAvailability,
  AllCausal,
  DefaultFromDate,
  MarketplaceDefaultValue,
  IsSaveRowKeysAction,
  configurationAvailabilitiesFilter,
  IsSetMovementTabVisible,
  IsSetAvailabilityTabVisible,
  MovementsSelectValue,
  AvailabilitiesSelectValue,
  configurationHiddenElements,
  IsLoadConfigurationAction,
  IsSaveConfigurationAction,
  IsSaveDefaultConfigurationsAction,
  IsDeleteConfigurationAction,
  IsRenameConfigurationAction,
  IsClearArticleInfoAction,
  SizeFilterDefaultValue,
  IsLoadProfileOptionsAction,
  HideMovement,
  HideAvailability,
  UiCodeEnableTabMovements,
  CheckValueForAvailability,
} from '../../Constants/Configuration'
import {
  decryptLocalStorageValue,
  encryptLocalStorageValue,
  getStorageValue,
  mapAvailabilitiesFilter,
  removeStorageValue,
  setStorageValue,
} from '../../Helpers/ConfigurationHelper'
import { WorkSpaces } from '../../Constants/WorkSpace'
import { createTimezoneCorrectedEpoch } from '../../Helpers/DateHelpers'

const movementsColumns = [
  {
    key: HAStoreName,
  },
  {
    key: DateHeader,
  },
  {
    key: DocumentationNumber,
  },
  {
    key: Causal,
  },
  {
    key: Status,
  },
  {
    key: Reference,
  },
  {
    key: Quantity,
  },
]

const availabilitesColumns = [
  {
    key: HAStoreName,
  },
  {
    key: DateHeader,
  },
  {
    key: DocumentationNumber,
  },
  {
    key: Causal,
  },
  {
    key: Status,
  },
  {
    key: Reference,
  },
  {
    key: CheckValueForAvailability,
  },
  {
    key: ProgressiveAvailability,
  },
]

const movementsUpdateLogColumns = [
  {
    key: HAStoreName,
  },
  {
    key: DateHeader,
  },
  {
    key: DocumentationNumber,
  },
  {
    key: Causal,
  },
  {
    key: Status,
  },
  {
    key: Reference,
  },
  {
    key: Quantity,
  },
  {
    key: UserDetail,
  },
  {
    key: EditDate,
  },
  {
    key: Sizes,
  },
  {
    key: OperationId,
  },
]

const availabilitiesUpdateLogColumns = [
  {
    key: HAStoreName,
  },
  {
    key: DateHeader,
  },
  {
    key: DocumentationNumber,
  },
  {
    key: Causal,
  },
  {
    key: Status,
  },
  {
    key: Reference,
  },
  {
    key: Quantity,
  },
  {
    key: ProgressiveAvailability,
  },
  {
    key: UserDetail,
  },
  {
    key: EditDate,
  },
  {
    key: OperationId,
  },
]

const availabilitiesFilter = [{ value: EnableMovementLog, active: false }]

export const movementsAvailabilitiesSlice = createSlice({
  name: 'movementsAvailabilities',
  initialState: {
    value: {
      availabilitiesColumns: availabilitesColumns,
      availabilitiesUpdateLogTableColumns: availabilitiesUpdateLogColumns,
      movementsColumns: movementsColumns,
      movementsUpdateTableColumns: movementsUpdateLogColumns,
      enableMovements: MovementsSelectValue,
      workspaceEnableMovements: MovementsSelectValue,
      availabilitiesFilter: availabilitiesFilter,
      size: SizeFilterDefaultValue,
      dateFrom: createTimezoneCorrectedEpoch(DefaultFromDate),
      dateTo: createTimezoneCorrectedEpoch(undefined),
      sortInfo: {}, // { columnKey: columnKey, order: order }
      filterCausal: AllCausal,
      selectedMarketplace: MarketplaceDefaultValue,
      zeroMovements: false,
      filters: [],
    },
  },
  reducers: {
    setEnableMovements: (state, action) => {
      state.value.enableMovements = action.payload
      state.value.filterCausal = AllCausal
      state.value.sortInfo = {}
    },
    saveAvailabilitiesFilter: (state, action) => {
      state.value.availabilitiesFilter = action.payload
    },
    setSize: (state, action) => {
      state.value.size = action.payload
    },
    setDateFrom: (state, action) => {
      state.value.dateFrom = action.payload
    },
    setDateTo: (state, action) => {
      state.value.dateTo = action.payload
    },
    setSortInfo: (state, action) => {
      state.value.sortInfo = {
        order: action.payload.order,
        columnKey: action.payload.columnKey,
      }
    },
    setFilterCausal: (state, action) => {
      state.value.filterCausal = action.payload
    },
    setSelectedMarketplace: (state, action) => {
      state.value.selectedMarketplace = action.payload
    },
    setZeroMovements: (state, action) => {
      state.value.zeroMovements = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(IsClearArticleInfoAction, (state, action) => {
        state.value.size = SizeFilterDefaultValue
        state.value.filterCausal = AllCausal
        state.value.enableMovements = action.payload.movementVisible ? MovementsSelectValue : AvailabilitiesSelectValue
        state.value.sortInfo = {}
        state.value.dateFrom = createTimezoneCorrectedEpoch(DefaultFromDate)
        state.value.dateTo = createTimezoneCorrectedEpoch(undefined)
        state.value.selectedMarketplace = MarketplaceDefaultValue
      })
      .addMatcher(IsLoadConfigurationAction, (state, action) => {
        const profileOptions = action.payload.defaultProfileOptions ? action.payload.defaultProfileOptions : []
        const mappedFilters = mapAvailabilitiesFilter(
          action.payload.configurationName,
          profileOptions,
          state.value.availabilitiesFilter,
          action.payload.user,
          action.payload.serverConfig,
        )
        state.value.availabilitiesFilter = mappedFilters

        const hiddenElements = decryptLocalStorageValue(
          action.payload.configurationName + configurationHiddenElements,
          action.payload.user,
          action.payload.serverConfig,
        )

        const isHiddenElementToSet = hiddenElements && hiddenElements.length > 0

        const isTabMovementVisible = isHiddenElementToSet ? hiddenElements.indexOf(HideMovement) === -1 : true
        const isTabAvailabilityVisibile = isHiddenElementToSet ? hiddenElements.indexOf(HideAvailability) === -1 : true

        state.value.workspaceEnableMovements =
          isHiddenElementToSet && (isTabMovementVisible || isTabAvailabilityVisibile)
            ? isTabMovementVisible
              ? MovementsSelectValue
              : AvailabilitiesSelectValue
            : MovementsSelectValue
      })
      .addMatcher(IsLoadProfileOptionsAction, (state, action) => {
        state.value.filters = mapAvailabilitiesFilter(
          action.payload.configurationName,
          action.payload.defaultProfileOptions,
          availabilitiesFilter,
          action.payload.user,
          action.payload.serverConfig,
        )

        const movementTabActive = action.payload.defaultProfileOptions.find(x => x.uiCode === UiCodeEnableTabMovements)

        state.value.enableMovements =
          movementTabActive && movementTabActive.active ? MovementsSelectValue : AvailabilitiesSelectValue
      })
      .addMatcher(IsSaveConfigurationAction, (state, action) => {
        const filterToSave = state.value.availabilitiesFilter.filter(x => x.active).map(x => x.value)

        encryptLocalStorageValue(
          action.payload.currentConfiguration + configurationAvailabilitiesFilter,
          JSON.stringify(filterToSave),
          action.payload.user,
          action.payload.serverConfig,
        )
      })
      .addMatcher(IsSaveDefaultConfigurationsAction, (state, action) => {
        if (action.payload.publicWorkspace) {
          const space = WorkSpaces[action.payload.publicWorkspace]
          encryptLocalStorageValue(
            action.payload.publicWorkspace + configurationAvailabilitiesFilter,
            JSON.stringify(space[configurationAvailabilitiesFilter.substring(1)]),
            action.payload.user,
            action.payload.serverConfig,
          )
        } else {
          for (const [key, value] of Object.entries(WorkSpaces)) {
            encryptLocalStorageValue(
              key + configurationAvailabilitiesFilter,
              JSON.stringify(value[configurationAvailabilitiesFilter.substring(1)]),
              action.payload.user,
              action.payload.serverConfig,
            )
          }
        }
      })
      .addMatcher(IsDeleteConfigurationAction, (state, action) => {
        removeStorageValue(
          action.payload.configurationName + configurationAvailabilitiesFilter,
          action.payload.user,
          action.payload.serverConfig,
        )
      })
      .addMatcher(IsRenameConfigurationAction, (state, action) => {
        const oldFilters = getStorageValue(
          action.payload.before + configurationAvailabilitiesFilter,
          action.payload.user,
          action.payload.serverConfig,
        )

        setStorageValue(
          action.payload.after + configurationAvailabilitiesFilter,
          action.payload.user,
          action.payload.serverConfig,
          oldFilters,
        )

        removeStorageValue(
          action.payload.before + configurationAvailabilitiesFilter,
          action.payload.user,
          action.payload.serverConfig,
        )
      })
      .addMatcher(IsSaveRowKeysAction, (state, action) => {
        state.value.size = SizeFilterDefaultValue
        state.value.filterCausal = AllCausal
        state.value.enableMovements = action.payload.movementVisible ? MovementsSelectValue : AvailabilitiesSelectValue
        state.value.sortInfo = {}
        state.value.dateFrom = createTimezoneCorrectedEpoch(DefaultFromDate)
        state.value.dateTo = createTimezoneCorrectedEpoch(undefined)
        state.value.selectedMarketplace = MarketplaceDefaultValue
      })
      .addMatcher(IsSetMovementTabVisible, (state, action) => {
        state.value.size = SizeFilterDefaultValue
        state.value.filterCausal = AllCausal
        state.value.enableMovements = AvailabilitiesSelectValue
        state.value.sortInfo = {}
        state.value.dateFrom = createTimezoneCorrectedEpoch(DefaultFromDate)
        state.value.dateTo = createTimezoneCorrectedEpoch(undefined)
        state.value.selectedMarketplace = MarketplaceDefaultValue
      })
      .addMatcher(IsSetAvailabilityTabVisible, (state, action) => {
        state.value.size = SizeFilterDefaultValue
        state.value.filterCausal = AllCausal
        state.value.enableMovements = MovementsSelectValue
        state.value.sortInfo = {}
        state.value.dateFrom = createTimezoneCorrectedEpoch(DefaultFromDate)
        state.value.dateTo = createTimezoneCorrectedEpoch(undefined)
        state.value.selectedMarketplace = MarketplaceDefaultValue
      })
  },
})

export const {
  setEnableMovements,
  saveAvailabilitiesFilter,
  setDateFrom,
  setDateTo,
  setSize,
  setSortInfo,
  setFilterCausal,
  setSelectedMarketplace,
  setZeroMovements,
} = movementsAvailabilitiesSlice.actions

export default movementsAvailabilitiesSlice.reducer
